import { memo, useCallback } from "react";
import { generatePath, Link } from "react-router";

import { PATHS } from "@/routes/paths";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { FormItem } from "@/components/ui/form/_base/form";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { RadioGroup, RadioGroupItem } from "@/components/ui/input/radio-group";

import { usePlanRoomStaticPrice } from "./hooks";

export const QuestionPlanRoomStaticPrice = memo(() => {
	const {
		hotelID,
		staticPriceParams,
		inUse,
		onChangeStaticPriceParams,
		onChangeStaticPriceAmountByRoomIDAndAdultNum,
	} = usePlanRoomStaticPrice();

	const roomPriceSettingsPath = generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, { hotelID: hotelID });

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const handleChangeStaticPriceParams = useCallback(async () => {
		showLoadingScreenCallback();

		await onChangeStaticPriceParams().finally(() => {
			hideLoadingScreenCallback();
		});
	}, [onChangeStaticPriceParams, showLoadingScreenCallback, hideLoadingScreenCallback]);

	const handleChangeStaticPriceAmountByRoomIDAndAdultNum = useCallback(
		(roomID: string, adultNum: number, amount: number | undefined) => {
			onChangeStaticPriceAmountByRoomIDAndAdultNum({ roomID, adultNum, amount });
		},
		[onChangeStaticPriceAmountByRoomIDAndAdultNum],
	);

	const guide = `このプランで販売するお部屋の料金の設定方法を選びましょう。<br />
定額を選ぶと、このプラン限定のお部屋の料金を設定することができます。`;

	return (
		<FeatureIslandCategoryQuestionForMain
			required={false}
			title="このプランに紐づく部屋の料金設定"
			guide={guide}
			isEdited={inUse}
			hasError={false}
			inputChildren={
				<div className="space-y-6">
					<div>
						<FormItem>
							<RadioGroup onValueChange={handleChangeStaticPriceParams} defaultValue={String(inUse)}>
								<div className="flex items-center space-x-3 space-y-0">
									<RadioGroupItem value="false" id="useStaticPrice_0" />
									<label htmlFor="useStaticPrice_0" className="pb-0.5">
										共通の部屋
										<Link
											to={roomPriceSettingsPath}
											className="mx-1 text-link-400 underline-offset-4 hover:underline"
											viewTransition
											target="_blank"
										>
											料金ランク
										</Link>
										もしくはサイコンでの設定に従う[一般的な設定]
									</label>
								</div>
								<div className="flex items-center space-x-3 space-y-0">
									<RadioGroupItem value="true" id="useStaticPrice_1" />
									<label htmlFor="useStaticPrice_1" className="pb-0.5">
										このプラン独自の部屋料金を設定する
									</label>
								</div>
							</RadioGroup>
						</FormItem>
					</div>
					<div className="space-y-4">
						{staticPriceParams?.staticRoomPrices?.value?.map((item) => {
							return (
								<div key={item.roomID.value} className="space-y-4 rounded border border-zinc-100 p-4">
									<p>{item.roomName.value}</p>

									<div className="ml-4 space-y-2 rounded">
										{item.prices &&
											item.prices.value &&
											item.prices.value.map((price) => {
												const key = `${item.roomID.value}-${price.adultNum.value}`;

												return (
													<div key={key} className="flex items-center gap-x-2">
														<p className="text-sm">
															大人{price.adultNum.value}人 の場合の合計料金
														</p>
														<InputNumberField
															value={price.price?.amount.value}
															handleInput={(input) =>
																handleChangeStaticPriceAmountByRoomIDAndAdultNum(
																	item.roomID.value,
																	price.adultNum.value,
																	input,
																)
															}
															errorMessage={price.price?.amount.error}
															hiddenSpinButton
														/>
														<p className="text-sm">円</p>
													</div>
												);
											})}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			}
		/>
	);
});

QuestionPlanRoomStaticPrice.displayName = "QuestionPlanRoomStaticPrice";
